import React, { useCallback, useState, useMemo } from 'react';
import { Box, Button, Typography, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import type { RootState } from '../store/store';
import {
  setActiveBands,
  setActiveHeatmap,
  setActiveHeight,
} from '../store/slices/servicePredictorSlice';
import {
  BAND_OPTIONS,
  HEATMAP_OPTIONS,
  HEIGHT_OPTIONS,
  UI_DIMENSIONS,
} from '../constants/uiConstants';
import { MAP_POSITION, MAP_Z_INDEX } from '../constants/mapConstants';

export interface FilterControlsProps {
  heatmapLoading?: boolean;
}

const FilterControls = ({
  heatmapLoading = false,
}: FilterControlsProps) => {
  const dispatch = useAppDispatch();
  const { activeBands, activeHeatmap, activeHeight } = useAppSelector(
    (state: RootState) => state.servicePredictor.filters
  );
  const [isMinimized, setIsMinimized] = useState(false);
  const bandOptions = BAND_OPTIONS;
  const heatmapOptions = HEATMAP_OPTIONS;
  const heightOptions = HEIGHT_OPTIONS;

  const handleBandClick = useCallback(
    (value: string) => {
      if (activeBands.includes(value)) {
        dispatch(setActiveBands(activeBands.filter((b) => b !== value)));
      } else {
        dispatch(setActiveBands([...activeBands, value]));
      }
    },
    [activeBands, dispatch]
  );

  const handleHeatmapClick = useCallback(
    (value: string) => {
      if (heatmapLoading) return;
      dispatch(setActiveHeatmap(activeHeatmap === value ? null : value));
    },
    [activeHeatmap, heatmapLoading, dispatch]
  );

  const handleHeightClick = useCallback(
    (value: string) => {
      if (heatmapLoading) return;
      dispatch(setActiveHeight(activeHeight === value ? null : value));
    },
    [activeHeight, heatmapLoading, dispatch]
  );

  const bandClickHandlers = useMemo(
    () =>
      bandOptions.reduce(
        (acc, option) => {
          acc[option.value] = () => handleBandClick(option.value);
          return acc;
        },
        {} as Record<string, () => void>
      ),
    [bandOptions, handleBandClick]
  );

  const heatmapClickHandlers = useMemo(
    () =>
      heatmapOptions.reduce(
        (acc, option) => {
          acc[option.value] = () => handleHeatmapClick(option.value);
          return acc;
        },
        {} as Record<string, () => void>
      ),
    [heatmapOptions, handleHeatmapClick]
  );

  const heightClickHandlers = useMemo(
    () =>
      heightOptions.reduce(
        (acc, option) => {
          acc[option.value] = () => handleHeightClick(option.value);
          return acc;
        },
        {} as Record<string, () => void>
      ),
    [heightOptions, handleHeightClick]
  );

  const handleExpand = useCallback(() => {
    setIsMinimized(false);
  }, []);

  const handleMinimize = useCallback(() => {
    setIsMinimized(true);
  }, []);

  if (isMinimized) {
    return (
      <Box
        sx={{
          position: 'absolute',
          top: MAP_POSITION.FILTER_CONTROLS_TOP,
          right: MAP_POSITION.FILTER_CONTROLS_RIGHT,
          zIndex: MAP_Z_INDEX.FILTER_CONTROLS,
        }}
      >
        <IconButton
          onClick={handleExpand}
          sx={{
            backgroundColor: 'background.paper',
            '&:hover': {
              backgroundColor: 'background.paper',
            },
          }}
          aria-label="Expand filters"
        >
          <FilterListIcon />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        top: MAP_POSITION.FILTER_CONTROLS_TOP,
        right: MAP_POSITION.FILTER_CONTROLS_RIGHT,
        backgroundColor: 'background.paper',
        borderRadius: 2,
        p: 2,
        zIndex: MAP_Z_INDEX.FILTER_CONTROLS,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        minWidth: UI_DIMENSIONS.FILTER_CONTROLS_MIN_WIDTH,
      }}
    >
      <IconButton
        size="small"
        onClick={handleMinimize}
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          p: 0.5,
          zIndex: MAP_Z_INDEX.FORM_PANEL_BUTTON,
        }}
        aria-label="Minimize filters"
      >
        <ExpandMoreIcon fontSize="small" />
      </IconButton>
      <Box>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 'bold',
            mb: 1,
            display: 'block',
            fontSize: '0.875rem',
          }}
        >
          Bands:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {bandOptions.map((option) => (
            <Button
              key={option.value}
              variant={
                activeBands.includes(option.value) ? 'contained' : 'outlined'
              }
              size="small"
              onClick={bandClickHandlers[option.value]}
              sx={{
                minWidth: 'auto',
                px: 1.5,
                py: 0.5,
                fontSize: '0.875rem',
              }}
            >
              {option.label}
            </Button>
          ))}
        </Box>
      </Box>

      <Box>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 'bold',
            mb: 1,
            display: 'block',
            fontSize: '0.875rem',
          }}
        >
          Heatmap:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {heatmapOptions.map((option) => {
            const isActive = activeHeatmap === option.value;
            const isDisabled = heatmapLoading && !isActive;

            return (
              <Button
                key={option.value}
                variant={isActive ? 'contained' : 'outlined'}
                size="small"
                onClick={heatmapClickHandlers[option.value]}
                disabled={isDisabled}
                sx={{
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.875rem',
                  opacity: isDisabled ? 0.5 : 1,
                }}
              >
                {option.label}
              </Button>
            );
          })}
        </Box>
      </Box>

      <Box>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 'bold',
            mb: 1,
            display: 'block',
            fontSize: '0.875rem',
          }}
        >
          Height:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {heightOptions.map((option) => {
            const isActive = activeHeight === option.value;
            const isDisabled = heatmapLoading && !isActive;

            return (
              <Button
                key={option.value}
                variant={isActive ? 'contained' : 'outlined'}
                size="small"
                onClick={heightClickHandlers[option.value]}
                disabled={isDisabled}
                sx={{
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.875rem',
                  opacity: isDisabled ? 0.5 : 1,
                }}
              >
                {option.label}
              </Button>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default FilterControls;
